/**
 * Dialog - modal shell built on the platform's `<dialog>` element.
 *
 * Everything a modal owes a keyboard or screen-reader user — `role="dialog"`,
 * `aria-modal`, a focus trap, focus restored to whatever opened it, Escape to
 * dismiss, and rendering above every stacking context — comes from
 * `showModal()`. None of it is implemented here, and that is the point: this
 * file used to be a bare `fixed inset-0` div that provided none of them
 * (shipcast-bj2), and the alternative to the platform was ~60 lines of
 * hand-rolled trap and restore logic that browsers already ship.
 *
 * The one thing that IS ours is the open/closed state. Every caller renders
 * this component conditionally, so React — not the user agent — decides whether
 * the dialog exists; see `onCancel` below.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Card, CardContent } from '@lean-agility/design-system/react';
import { DialogHeader } from './DialogHeader';
import { cn } from '@lean-agility/design-system/react';

export interface DialogProps {
  /**
   * Dialog content.
   */
  children: ReactNode;
  /**
   * Dialog title. A DialogHeader will be automatically rendered, and the title
   * becomes the dialog's accessible name.
   */
  title: ReactNode;
  /**
   * Callback when the dialog is closed, via the close button or Escape.
   */
  onClose?: () => void;
  /**
   * Whether Escape may dismiss the dialog.
   *
   * Pass `false` while a submit is in flight so the dialog cannot vanish out
   * from under a request it started. The close button is a separate affordance
   * — disable it by omitting `onClose`.
   * @default true
   */
  dismissible?: boolean;
  /**
   * Additional CSS classes for the dialog container.
   */
  className?: string;
  /**
   * Additional CSS classes for the content container.
   */
  contentClassName?: string;
  /**
   * Maximum width of the dialog.
   * @default "max-w-md"
   */
  maxWidth?: string;
}

/**
 * Undo the user-agent stylesheet, which paints `<dialog>` as a bordered box
 * with its own background — the Card inside is the visible surface.
 *
 * `overflow` is deliberately left at the UA's `auto`: content taller than the
 * viewport becomes scrollable instead of being clipped off-screen with no way
 * to reach it, which is what the old `fixed inset-0` overlay did.
 *
 * `m-auto` is load-bearing, not decoration. A modal dialog is centred by the
 * UA rule `dialog:modal { inset: 0; margin: auto }`, and Tailwind's preflight
 * zeroes `margin` on `*` — including `::backdrop` — so without this the dialog
 * pins itself to the top-left corner. Nothing in the test suite notices, since
 * no assertion looks at geometry; `e2e/dialog-modality.spec.ts` now has one
 * that does.
 */
const RESET_UA_CHROME = 'm-auto w-full border-0 bg-transparent p-0 text-inherit';

export function Dialog({
  children,
  title,
  onClose,
  dismissible = true,
  className,
  contentClassName,
  maxWidth = 'max-w-md',
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  // Modality is a property of *how* the element was opened, not of markup —
  // there is no attribute for it, so it has to be requested imperatively.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Restoring focus is the one part of modality the platform cannot do for
    // us here. A user agent restores it while closing the dialog, but React
    // detaches the element *before* running this cleanup, and by then the
    // browser has already dropped focus to <body>. Capturing the opener is
    // also the only route that survives a dismissal we never see — a Cancel
    // button wired straight to the parent's state, or a route change.
    const opener = document.activeElement;
    if (!node.open) node.showModal();

    return () => {
      if (node.open) node.close();
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(event) => {
        // Were the browser allowed to close the element itself, React would go
        // on rendering an `open=false` dialog: mounted, invisible, unreachable.
        // So Escape is reported upward and acted on by unmounting us.
        event.preventDefault();
        if (dismissible) onClose?.();
      }}
      className={cn(RESET_UA_CHROME, 'backdrop:bg-black/50', maxWidth, className)}
    >
      <Card className="w-full">
        <DialogHeader titleId={titleId} title={title} onClose={onClose} />
        <CardContent className={cn(contentClassName)}>{children}</CardContent>
      </Card>
    </dialog>
  );
}
